/**
 * Decision wheel — parse options, settings, and a fair landing angle.
 * The canvas lives in the web app; this stays DOM-free for a later Expo table.
 */

import { LIMITS } from "./api.js";

export const KOLO_MAX_OPTIONS = LIMITS.koloOptions.max;
export const KOLO_MIN_OPTIONS = LIMITS.koloOptions.min;

const TAU = Math.PI * 2;
/** Pointer sits at 12 o'clock on the canvas. */
export const KOLO_POINTER_ANGLE = -Math.PI / 2;

export type KoloSpinSpeed = "short" | "normal" | "long";
export type KoloExtraTurns = "few" | "normal" | "many";
export type KoloPalette = "site" | "vivid";

export interface KoloSettings {
  speed: KoloSpinSpeed;
  extraTurns: KoloExtraTurns;
  sound: boolean;
  autoRemoveWinner: boolean;
  palette: KoloPalette;
}

export const KOLO_DEFAULT_SETTINGS: KoloSettings = {
  speed: "normal",
  extraTurns: "normal",
  sound: true,
  autoRemoveWinner: false,
  palette: "site",
};

export function koloSpinDurationMs(speed: KoloSpinSpeed): number {
  if (speed === "short") return 2500;
  if (speed === "long") return 8000;
  return 4500;
}

export function koloExtraTurnCount(extra: KoloExtraTurns): number {
  if (extra === "few") return 3;
  if (extra === "many") return 8;
  return 5;
}

export function parseKoloLine(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  const max = LIMITS.koloOption.max;
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

/** Split on commas, semicolons, or newlines. Keeps duplicates (two people, same name). */
export function parseKoloOptions(raw: string): string[] {
  const options: string[] = [];
  for (const part of raw.split(/[\n,;]+/)) {
    const value = parseKoloLine(part);
    if (!value) continue;
    options.push(value);
    if (options.length >= KOLO_MAX_OPTIONS) break;
  }
  return options;
}

export function koloSliceStep(count: number): number {
  return count > 0 ? TAU / count : TAU;
}

/**
 * Random slice, random rest position inside it, plus extra full turns so the
 * wheel always spins forward.
 */
export function planKoloSpin(
  count: number,
  startAngle: number,
  extraTurns = 5,
  rng: () => number = Math.random,
): { index: number; targetAngle: number } {
  const step = koloSliceStep(count);
  const index = Math.min(count - 1, Math.floor(rng() * count));
  const landing = index * step + rng() * step;
  const endMod = ((KOLO_POINTER_ANGLE - landing) % TAU + TAU) % TAU;
  const startMod = ((startAngle % TAU) + TAU) % TAU;
  let delta = (endMod - startMod + TAU) % TAU;
  delta += extraTurns * TAU;
  return { index, targetAngle: startAngle + delta };
}
